/**
 * search.ts — применение поиска (серверный) + подсказки (H.1)
 * Проект: TorgiRT (shared — все витрины)
 *
 * v2: Поиск выполняется на сервере через fetchProducts({ q: ... }).
 *     Debounce 300ms при вводе, мгновенный при Enter/кнопке.
 * v3 (H.1): dropdown подсказок при фокусе на пустое поле поиска.
 */

import { state, refs, BASE_PATH } from './state';
import { fetchProducts } from './catalog';
import { runCatalogFetchThen } from './catalog-loading';
import { pushUrlState, replaceUrlState } from './url';
import { renderProductsCount, renderProducts, renderPagination, renderFilters } from './render';
import { getConfig } from './config';
import { isCatalogListingPage, lockBodyScroll, unlockBodyScroll } from './helpers';
import { closeDrawer } from './drawer';

let _searchTimer: ReturnType<typeof setTimeout> = 0;
let _hintsLoaded = false;
let _hints: string[] = [];
let _hintsDropdown: HTMLElement | null = null;

const SEARCH_DEBOUNCE_MS = 300;
const HINTS_MAX_COUNT = 8;

/** Full navigation to search results (DES-S-06 H3 — off listing pages). */
export function navigateToSearch(q: string): void {
  const trimmed = q.trim();
  if (!trimmed) return;
  window.location.href = BASE_PATH + "/search?q=" + encodeURIComponent(trimmed);
}

/**
 * Единая функция применения поиска.
 */
export function applySearch(immediate?: boolean): void {
  if (refs.searchInput) {
    state.searchQuery = (refs.searchInput as HTMLInputElement).value;
  }
  closeHintsDropdown();
  clearTimeout(_searchTimer);

  if (!isCatalogListingPage()) {
    if (immediate) {
      navigateToSearch((refs.searchInput as HTMLInputElement | undefined)?.value ?? state.searchQuery);
    }
    return;
  }

  state.searchMode = "strict";
  state.currentPage = 1;

  if (immediate) {
    void doServerSearch().then(() => {
      pushUrlState();
      hideSearchOverlay();
    });
  } else {
    _searchTimer = setTimeout(() => {
      void doServerSearch().then(() => {
        const hadSearch = !!(new URLSearchParams(window.location.search)).get("q");
        if (hadSearch) { replaceUrlState(); } else { pushUrlState(); }
      });
    }, SEARCH_DEBOUNCE_MS);
  }
}

/**
 * Выполнить серверный поиск и перерисовать UI.
 */
async function doServerSearch(): Promise<void> {
  await runCatalogFetchThen(() => fetchProducts({ q: state.searchQuery, page: 1 }), () => {
    renderProductsCount();
    renderProducts();
    renderPagination();
    renderFilters();
  });
}

export function handleSearchInput(): void {
  applySearch(false);
}

/* ── Search hints (H.1) ── */

async function loadHints(): Promise<void> {
  if (_hintsLoaded) return;
  _hintsLoaded = true;
  const url = getConfig('catalogTreeUrl', '')?.toString().replace('/tree', '/hints');
  if (!url) return;
  try {
    const resp = await fetch(url);
    if (!resp.ok) return;
    const data = await resp.json();
    _hints = Array.isArray(data?.hints) ? data.hints.slice(0, HINTS_MAX_COUNT) : [];
  } catch { /* graceful: no hints */ }
}

function syncSearchCombobox(expanded: boolean): void {
  const input = refs.searchInput;
  if (!input) return;
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  if (_hintsDropdown?.id) {
    input.setAttribute('aria-controls', _hintsDropdown.id);
  }
}

function createHintsDropdown(): HTMLElement {
  if (_hintsDropdown) return _hintsDropdown;
  _hintsDropdown = document.createElement('div');
  _hintsDropdown.id = 'search-hints-listbox';
  _hintsDropdown.className = 'search-hints-dropdown hidden';
  _hintsDropdown.setAttribute('role', 'listbox');
  _hintsDropdown.setAttribute('aria-label', 'Подсказки поиска');
  refs.searchInput?.parentElement?.appendChild(_hintsDropdown);
  syncSearchCombobox(false);
  return _hintsDropdown;
}

function showHintsDropdown(): void {
  if (_hints.length === 0 || state.searchQuery.trim().length > 0) return;
  const dd = createHintsDropdown();
  dd.innerHTML = _hints.map(h =>
    `<button type="button" class="search-hint-item" data-action="search-hint" data-hint="${h.replace(/"/g, '&quot;')}" role="option">${h.replace(/</g, '&lt;')}</button>`
  ).join('');
  dd.classList.remove('hidden');
  syncSearchCombobox(true);
}

function closeHintsDropdown(): void {
  _hintsDropdown?.classList.add('hidden');
  syncSearchCombobox(false);
}

export function initSearchHints(): void {
  if (!refs.searchInput) return;

  refs.searchInput.addEventListener('focus', () => {
    void loadHints().then(showHintsDropdown);
  });

  refs.searchInput.addEventListener('input', () => {
    if ((refs.searchInput as HTMLInputElement).value.trim().length > 0) {
      closeHintsDropdown();
    } else {
      showHintsDropdown();
    }
  });

  document.addEventListener('click', (e: MouseEvent) => {
    if (!_hintsDropdown) return;
    const target = e.target as HTMLElement;
    if (!refs.searchInput?.contains(target) && !_hintsDropdown.contains(target)) {
      closeHintsDropdown();
    }
  });
}

/* ── Mobile search overlay (DES-S-06 H4) ── */

let _searchForm: HTMLFormElement | null = null;
let _searchFormParent: HTMLElement | null = null;
let _searchOverlay: HTMLElement | null = null;
let _searchOverlayInner: HTMLElement | null = null;
let _searchTrigger: HTMLButtonElement | null = null;
let _overlayOpen = false;

const OVERLAY_FOCUSABLE_SEL =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function trapOverlayFocus(event: KeyboardEvent): void {
  if (!_overlayOpen || !_searchOverlay || event.key !== 'Tab') return;
  const focusables = Array.from(
    _searchOverlay.querySelectorAll<HTMLElement>(OVERLAY_FOCUSABLE_SEL)
  ).filter((el) => el.offsetParent !== null || el === document.activeElement);
  if (focusables.length === 0) {
    event.preventDefault();
    return;
  }
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement as HTMLElement | null;
  if (event.shiftKey) {
    if (!active || active === first || !_searchOverlay.contains(active)) {
      event.preventDefault();
      last.focus();
    }
  } else if (!active || active === last || !_searchOverlay.contains(active)) {
    event.preventDefault();
    first.focus();
  }
}

function onOverlayEscape(event: KeyboardEvent): void {
  if (event.key === 'Escape' && _overlayOpen) {
    hideSearchOverlay();
  }
}

export function showSearchOverlay(): void {
  if (!_searchOverlay || !_searchForm || !_searchFormParent || _overlayOpen) return;
  if (!window.matchMedia('(max-width: 767px)').matches) return;

  closeDrawer();

  const mount = _searchOverlayInner ?? _searchOverlay;
  mount.appendChild(_searchForm);

  _searchOverlay.removeAttribute('hidden');
  _searchOverlay.classList.add('is-open');
  _overlayOpen = true;

  lockBodyScroll();

  void loadHints().then(showHintsDropdown);
  (refs.searchInput as HTMLInputElement | undefined)?.focus();

  document.addEventListener('keydown', trapOverlayFocus, true);
  document.addEventListener('keydown', onOverlayEscape);
}

export function hideSearchOverlay(): void {
  if (!_searchOverlay || !_searchForm || !_searchFormParent || !_overlayOpen) return;

  document.removeEventListener('keydown', trapOverlayFocus, true);
  document.removeEventListener('keydown', onOverlayEscape);

  closeHintsDropdown();
  _searchFormParent.appendChild(_searchForm);
  _searchOverlay.classList.remove('is-open');
  _searchOverlay.setAttribute('hidden', '');
  _overlayOpen = false;

  unlockBodyScroll();
  _searchTrigger?.focus();
}

export function initSearchOverlay(): void {
  _searchOverlay = document.getElementById('search-overlay');
  _searchTrigger = document.querySelector('.header-search-trigger') as HTMLButtonElement | null;
  _searchForm = document.querySelector('.header-search-zone .header-search-wrap') as HTMLFormElement | null;
  _searchFormParent = document.querySelector('.header-search-zone') as HTMLElement | null;
  _searchOverlayInner = _searchOverlay?.querySelector('.search-overlay-inner') as HTMLElement | null;

  if (!_searchOverlay || !_searchTrigger || !_searchForm || !_searchFormParent) return;

  document.documentElement.classList.add('js-search-overlay-ready');

  document.addEventListener('torgirt:close-search-overlay', () => {
    hideSearchOverlay();
  });

  const tabletMQ = window.matchMedia('(min-width: 768px)');
  function onTabletUp(mq: MediaQueryListEvent | MediaQueryList): void {
    if (mq.matches) hideSearchOverlay();
  }
  if (tabletMQ.addEventListener) {
    tabletMQ.addEventListener('change', onTabletUp);
  }
}
